import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Building2, User, Server, Scale, FileText, Globe, Mail, Phone } from 'lucide-react';

const LegalNotice = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-black min-h-screen">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            <Link
              to="/"
              className="text-2xl font-semibold tracking-tight text-white hover:text-accent transition-colors duration-300"
            >
              webysee<span className="text-accent">.</span>
            </Link>
            <Link
              to="/"
              className="flex items-center gap-2 text-white/60 hover:text-white transition-colors duration-300"
            >
              <ArrowLeft size={18} />
              Retour à l'accueil
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-[0.02]" />
        <div className="absolute top-1/4 left-0 w-[500px] h-[500px] bg-violet-500/5 rounded-full blur-[200px]" />
        
        <div className="max-w-[900px] mx-auto px-6 lg:px-12 relative z-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-accent/10 flex items-center justify-center">
              <Scale className="w-6 h-6 text-accent" />
            </div>
            <span className="text-accent text-sm font-medium uppercase tracking-wider">Informations légales</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-semibold text-white mb-6">
            Mentions légales
          </h1>
          <p className="text-white/60 text-lg leading-relaxed">
            Conformément aux dispositions de la loi n° 2004-575 du 21 juin 2004 pour la confiance en l'économie numérique, 
            voici les informations légales concernant le site webysee.fr.
          </p>
          <p className="text-white/40 text-sm mt-6">
            Dernière mise à jour : Janvier 2025
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-32">
        <div className="max-w-[900px] mx-auto px-6 lg:px-12">
          <div className="space-y-8">
            
            {/* Éditeur du site */}
            <div className="bg-white/[0.02] border border-white/[0.05] p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-10 h-10 bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-5 h-5 text-accent" />
                </div>
                <h2 className="text-xl font-semibold text-white">1. Éditeur du site</h2>
              </div>
              <div className="pl-14 space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-white/40 text-sm mb-1">Raison sociale</p>
                    <p className="text-white font-medium">Webysee</p>
                  </div>
                  <div>
                    <p className="text-white/40 text-sm mb-1">Forme juridique</p>
                    <p className="text-white font-medium">Micro-entreprise / Auto-entrepreneur</p>
                  </div>
                  <div>
                    <p className="text-white/40 text-sm mb-1">SIRET</p>
                    <p className="text-white font-medium">XXX XXX XXX XXXXX</p>
                  </div>
                  <div>
                    <p className="text-white/40 text-sm mb-1">Capital social</p>
                    <p className="text-white font-medium">N/A (Micro-entreprise)</p>
                  </div>
                </div>
                <div className="pt-4 border-t border-white/[0.05]">
                  <p className="text-white/40 text-sm mb-1">Siège social</p>
                  <p className="text-white">France</p>
                </div>
              </div>
            </div>

            {/* Directeur de publication */}
            <div className="bg-white/[0.02] border border-white/[0.05] p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-10 h-10 bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-accent" />
                </div>
                <h2 className="text-xl font-semibold text-white">2. Directeur de publication</h2>
              </div>
              <div className="pl-14 space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-white/40 text-sm mb-1">Responsable</p>
                    <p className="text-white font-medium">Gérant de Webysee</p>
                  </div>
                  <div>
                    <p className="text-white/40 text-sm mb-1">Qualité</p>
                    <p className="text-white font-medium">Directeur de la publication</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact */}
            <div className="bg-white/[0.02] border border-white/[0.05] p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-10 h-10 bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-accent" />
                </div>
                <h2 className="text-xl font-semibold text-white">3. Contact</h2>
              </div>
              <div className="pl-14">
                <div className="grid md:grid-cols-2 gap-6">
                  <a 
                    href="mailto:contact@webysee.com"
                    className="flex items-center gap-3 p-4 bg-white/[0.02] border border-white/[0.05] hover:border-accent/30 transition-colors group"
                  >
                    <Mail className="w-5 h-5 text-accent" />
                    <div>
                      <p className="text-white/40 text-xs">Email</p>
                      <p className="text-white group-hover:text-accent transition-colors">contact@webysee.com</p>
                    </div>
                  </a>
                  <a 
                    href="tel:0623880881"
                    className="flex items-center gap-3 p-4 bg-white/[0.02] border border-white/[0.05] hover:border-accent/30 transition-colors group"
                  >
                    <Phone className="w-5 h-5 text-accent" />
                    <div>
                      <p className="text-white/40 text-xs">Téléphone</p>
                      <p className="text-white group-hover:text-accent transition-colors">06 23 88 08 81</p>
                    </div>
                  </a>
                </div>
              </div>
            </div>

            {/* Hébergement */}
            <div className="bg-white/[0.02] border border-white/[0.05] p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-10 h-10 bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <Server className="w-5 h-5 text-accent" />
                </div>
                <h2 className="text-xl font-semibold text-white">4. Hébergement</h2>
              </div>
              <div className="pl-14 space-y-4">
                <p className="text-white/60">
                  Ce site est hébergé par :
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-white/40 text-sm mb-1">Hébergeur</p>
                    <p className="text-white font-medium">Emergent</p>
                  </div>
                  <div>
                    <p className="text-white/40 text-sm mb-1">Site web</p>
                    <p className="text-white font-medium">emergent.sh</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Propriété intellectuelle */}
            <div className="bg-white/[0.02] border border-white/[0.05] p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-10 h-10 bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-accent" />
                </div>
                <h2 className="text-xl font-semibold text-white">5. Propriété intellectuelle</h2>
              </div>
              <div className="pl-14 space-y-4 text-white/60">
                <p>
                  L'ensemble du contenu de ce site (textes, images, vidéos, logos, icônes, sons, logiciels, etc.) 
                  est la propriété exclusive de Webysee ou de ses partenaires et est protégé par les lois françaises 
                  et internationales relatives à la propriété intellectuelle.
                </p>
                <p>
                  Toute reproduction, représentation, modification, publication, transmission, dénaturation, totale ou partielle 
                  du site ou de son contenu, par quelque procédé que ce soit, et sur quelque support que ce soit est interdite 
                  sans l'autorisation écrite préalable de Webysee.
                </p>
                <p>
                  Toute exploitation non autorisée du site ou de son contenu sera considérée comme constitutive d'une contrefaçon 
                  et poursuivie conformément aux dispositions des articles L.335-2 et suivants du Code de Propriété Intellectuelle.
                </p>
              </div>
            </div>

            {/* Limitation de responsabilité */}
            <div className="bg-white/[0.02] border border-white/[0.05] p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-10 h-10 bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <Scale className="w-5 h-5 text-accent" />
                </div>
                <h2 className="text-xl font-semibold text-white">6. Limitation de responsabilité</h2>
              </div>
              <div className="pl-14 space-y-4 text-white/60">
                <p>
                  Webysee s'efforce d'assurer au mieux de ses possibilités l'exactitude et la mise à jour des informations 
                  diffusées sur ce site. Toutefois, Webysee ne peut garantir l'exactitude, la précision ou l'exhaustivité 
                  des informations mises à disposition sur ce site.
                </p>
                <p>
                  En conséquence, Webysee décline toute responsabilité :
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Pour toute imprécision, inexactitude ou omission portant sur des informations disponibles sur le site</li>
                  <li>Pour tous dommages résultant d'une intrusion frauduleuse d'un tiers</li>
                  <li>Pour tous dommages, directs ou indirects, résultant de l'accès ou de l'utilisation du site</li>
                </ul>
              </div>
            </div>

            {/* Liens hypertextes */}
            <div className="bg-white/[0.02] border border-white/[0.05] p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-10 h-10 bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <Globe className="w-5 h-5 text-accent" />
                </div>
                <h2 className="text-xl font-semibold text-white">7. Liens hypertextes</h2>
              </div>
              <div className="pl-14 space-y-4 text-white/60">
                <p>
                  Le site peut contenir des liens hypertextes vers d'autres sites. Webysee n'exerce aucun contrôle 
                  sur ces sites et décline toute responsabilité quant à leur contenu ou aux services qui y sont proposés.
                </p>
                <p>
                  La création de liens hypertextes vers le site webysee.fr est soumise à l'accord préalable 
                  du directeur de la publication.
                </p>
              </div>
            </div>

            {/* Droit applicable */}
            <div className="bg-white/[0.02] border border-white/[0.05] p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-10 h-10 bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <Scale className="w-5 h-5 text-accent" />
                </div>
                <h2 className="text-xl font-semibold text-white">8. Droit applicable</h2>
              </div>
              <div className="pl-14 text-white/60">
                <p>
                  Les présentes mentions légales sont régies par le droit français. En cas de litige, 
                  et après échec de toute tentative de recherche d'une solution amiable, les tribunaux français 
                  seront seuls compétents pour connaître de ce litige.
                </p>
              </div>
            </div>

          </div>

          {/* Contact Box */}
          <div className="mt-16 bg-gradient-to-br from-accent/10 to-accent/5 border border-accent/20 p-8">
            <h3 className="text-xl font-semibold text-white mb-4">Une question ?</h3>
            <p className="text-white/60 mb-6">
              Pour toute question concernant ces mentions légales, n'hésitez pas à nous contacter.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="mailto:contact@webysee.com"
                className="inline-flex items-center gap-2 px-6 py-3 bg-accent text-black font-medium hover:bg-accent/90 transition-colors"
              >
                <Mail size={18} />
                contact@webysee.com
              </a>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white/5 text-white border border-white/10 hover:bg-white/10 transition-colors"
              >
                <ArrowLeft size={18} />
                Retour à l'accueil
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/[0.05] py-8">
        <div className="max-w-[900px] mx-auto px-6 lg:px-12 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/40 text-sm">
            © {new Date().getFullYear()} Webysee. Tous droits réservés.
          </p>
          <div className="flex gap-6">
            <Link to="/mentions-legales" className="text-accent text-sm">
              Mentions légales
            </Link>
            <Link to="/politique-confidentialite" className="text-white/40 hover:text-white text-sm transition-colors">
              Politique de confidentialité
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LegalNotice;
